import type React from 'react';

/**
 * Inline SVG icons for contact items. These render as part of the HTML
 * in renderToStaticMarkup and work in the iframe/PDF pipeline.
 *
 * All icons use a 24x24 viewBox, currentColor fill, and are sized
 * via the parent's font-size (1em).
 */

interface IconProps {
  className?: string;
}

function Svg({ d, className }: { d: string; className?: string | undefined }) {
  return (
    <svg
      className={className ?? 'cv-contact-icon'}
      viewBox="0 0 24 24"
      fill="currentColor"
      width="1em"
      height="1em"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const icons: Record<string, (props: IconProps) => React.JSX.Element> = {
  // Standard contact
  email: (p) => (
    <Svg
      className={p.className}
      d="M2 6a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm2.5 0L12 11l7.5-5h-15zM4 8.5V18h16V8.5l-8 5.33L4 8.5z"
    />
  ),
  phone: (p) => (
    <Svg
      className={p.className}
      d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.02l-2.2 2.2z"
    />
  ),
  location: (p) => (
    <Svg
      className={p.className}
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"
    />
  ),
  website: (p) => (
    <Svg
      className={p.className}
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1a2 2 0 002 2v1.93zm6.9-2.54A1.99 1.99 0 0016 16h-1v-3a1 1 0 00-1-1H8v-2h2a1 1 0 001-1V7h2a2 2 0 002-2v-.41a7.984 7.984 0 013.9 9.8z"
    />
  ),

  // Social networks
  github: (p) => (
    <Svg
      className={p.className}
      d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.167 6.839 9.49.5.092.682-.217.682-.482 0-.237-.009-.866-.013-1.7-2.782.604-3.369-1.341-3.369-1.341-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844a9.59 9.59 0 012.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z"
    />
  ),
  linkedin: (p) => (
    <Svg
      className={p.className}
      d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14zm-.5 15.5v-5.3a3.26 3.26 0 00-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 011.4 1.4v4.93h2.79zM6.88 8.56a1.68 1.68 0 001.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 00-1.69 1.69c0 .93.76 1.68 1.69 1.68zm1.39 9.94v-8.37H5.5v8.37h2.77z"
    />
  ),
  xing: (p) => (
    <Svg
      className={p.className}
      d="M18.188 2H5.812A.812.812 0 005 2.812v18.376c0 .449.363.812.812.812h12.376a.812.812 0 00.812-.812V2.812A.812.812 0 0018.188 2zM8.7 14.l-2.7-4.2h2.1l1.4 2.4-2.2 3.8H5.2L8.7 14zm8.1 5.6h-2.1l-3.5-6.4 2.2-3.9h2.1l-2.2 3.9 3.5 6.4z"
    />
  ),
  twitter: (p) => (
    <Svg
      className={p.className}
      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
    />
  ),
  instagram: (p) => (
    <Svg
      className={p.className}
      d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2zm-.2 2A3.6 3.6 0 004 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6zm9.65 1.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z"
    />
  ),
  gitlab: (p) => (
    <Svg
      className={p.className}
      d="M21.94 12.865l-1.066-3.28-.001-.003-2.1-6.463a.477.477 0 00-.907.005l-2 6.153H8.134l-2-6.153a.477.477 0 00-.907-.005L3.127 9.582l-.001.003-1.066 3.28a.685.685 0 00.249.764l9.64 7.008a.09.09 0 00.102 0l9.64-7.008a.685.685 0 00.249-.764z"
    />
  ),
  stackoverflow: (p) => (
    <Svg
      className={p.className}
      d="M15.725 0l-1.72 1.277 6.39 8.588 1.72-1.277L15.726 0zm-3.94 3.418l-1.369 1.644 8.225 6.85 1.369-1.644-8.225-6.85zm-3.15 4.465l-.905 1.94 9.702 4.517.905-1.94-9.702-4.517zm-1.85 4.86l-.44 2.093 10.473 2.201.44-2.092-10.473-2.203zM1.89 15.47V24h19.19v-8.53h-2.133v6.397H4.021v-6.396H1.89zm4.265 2.133v2.13h10.66v-2.13H6.154z"
    />
  ),
  dribbble: (p) => (
    <Svg
      className={p.className}
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c5.51 0 10-4.48 10-10S17.51 2 12 2zm6.605 4.61a8.502 8.502 0 011.93 5.314c-.281-.054-3.101-.629-5.943-.271-.065-.141-.12-.293-.184-.445a25.424 25.424 0 00-.564-1.236c3.145-1.28 4.577-3.124 4.761-3.362zM12 3.475c2.17 0 4.154.813 5.662 2.148-.152.216-1.443 1.941-4.48 3.08-1.399-2.57-2.95-4.675-3.189-5A8.687 8.687 0 0112 3.475z"
    />
  ),
  youtube: (p) => (
    <Svg
      className={p.className}
      d="M21.543 6.498C22 8.28 22 12 22 12s0 3.72-.457 5.502c-.254.985-.997 1.76-1.938 2.022C17.896 20 12 20 12 20s-5.893 0-7.605-.476c-.945-.266-1.687-1.04-1.938-2.022C2 15.72 2 12 2 12s0-3.72.457-5.502c.254-.985.997-1.76 1.938-2.022C6.107 4 12 4 12 4s5.896 0 7.605.476c.945.266 1.687 1.04 1.938 2.022zM10 15.5l6-3.5-6-3.5v7z"
    />
  ),
  behance: (p) => (
    <Svg
      className={p.className}
      d="M7.8 10.5H2.5V6h5.1c2.3 0 3.5 1.1 3.5 2.4 0 1.2-1 2.1-3.3 2.1zM2.5 12h5.6c2.6 0 3.7 1 3.7 2.5 0 1.6-1.3 2.5-3.8 2.5H2.5V12zm12.3-1.7c-1.5 0-2.5 1.2-2.5 3s1 3 2.5 3c1.2 0 2-.6 2.3-1.8h2.2c-.4 2.2-2 3.5-4.5 3.5-3 0-4.8-2-4.8-4.7s1.8-4.7 4.8-4.7c2.7 0 4.4 1.8 4.4 4.2v.5h-6.5c.1 1.5 1 2 2.1 2 .8 0 1.5-.4 1.7-1.1h2.3zM15 4h5v1.5h-5V4z"
    />
  ),
};

/**
 * Resolve an icon component by network name (case-insensitive).
 * Returns undefined if no matching icon exists.
 * @param network
 * @returns The icon component function, or undefined if no matching icon exists
 */
export function getContactIcon(
  network: string,
): ((props: IconProps) => React.JSX.Element) | undefined {
  return icons[network.toLowerCase()];
}

/** All known network names that have icons. */
export const KNOWN_NETWORKS = Object.keys(icons);
